/**
 * Collaborator avatars + "Invite Admin" button for the builder header.
 * Shows who is on the program (creator first, pending invitees faded) and who
 * is in the editor right now (orange ring).
 */
import React, { useState } from 'react';
import { Avatar, AvatarGroup, Box, Tooltip } from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import InviteAdminDialog from './InviteAdminDialog';
import { useProgramCollaborators } from './useProgramCollaborators';
import { useEditLock } from './ProgramEditLockProvider';
import { EDIT_LOCK_EXEMPT } from './EditLockGuard';
import type { ProgramCollaborator } from '../../api';

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || '?';

// "Jane Doe (you)" for yourself; otherwise the roles they hold on the program.
const avatarLabel = (c: ProgramCollaborator, inEditor: boolean) => {
  const roles = c.isYou
    ? ['you']
    : [c.isCreator && 'creator', c.isOwner && 'owner', !c.isCreator && !c.isOwner && 'admin'].filter(Boolean);
  const extra = [c.pending && 'invited, not signed up yet', inEditor && 'in the editor'].filter(Boolean);
  return `${c.name} (${roles.join(', ')})${extra.length ? ` — ${extra.join(' · ')}` : ''}`;
};

const CollaboratorBar: React.FC<{ grpId: string; programName?: string }> = ({ grpId, programName }) => {
  const { collaborators, setCollaborators } = useProgramCollaborators(grpId);
  const { status, holder } = useEditLock();
  const [open, setOpen] = useState(false);
  if (!grpId) return null;

  const editorId = status === 'locked' ? holder?.userId : undefined;

  return (
    <Box {...EDIT_LOCK_EXEMPT} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {collaborators.length > 0 && (
        <AvatarGroup
          max={6}
          sx={{
            '& .MuiAvatar-root': {
              width: 28,
              height: 28,
              fontSize: 11,
              fontFamily: 'Inter',
              border: '2px solid #0F0F0F',
              bgcolor: '#2A2A2A',
              color: '#FDFDFD'
            }
          }}
        >
          {collaborators.map((c) => {
            const label = avatarLabel(c, Boolean(c.userId && c.userId === editorId));
            return (
              <Tooltip key={c.userId || c.email} title={label}>
                <Avatar
                  src={c.headshot || undefined}
                  alt={c.name}
                  sx={{
                    opacity: c.pending ? 0.5 : 1,
                    ...(c.userId && c.userId === editorId ? { borderColor: '#F79420 !important' } : {})
                  }}
                >
                  {initials(c.name)}
                </Avatar>
              </Tooltip>
            );
          })}
        </AvatarGroup>
      )}
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          background: 'transparent',
          border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
          borderRadius: '8px',
          px: 1.75,
          py: 0.875,
          cursor: 'pointer',
          color: 'var(--Text-text-primary, #FDFDFD)',
          fontFamily: 'Inter',
          fontSize: 13,
          fontWeight: 500,
          whiteSpace: 'nowrap',
          '&:hover': { borderColor: 'var(--Stroke-highlight, #F79420)' }
        }}
      >
        <PersonAddAltOutlinedIcon sx={{ fontSize: 17 }} />
        Invite Admin
      </Box>
      <InviteAdminDialog
        open={open}
        grpId={grpId}
        programName={programName}
        collaborators={collaborators}
        onClose={() => setOpen(false)}
        onInvited={setCollaborators}
      />
    </Box>
  );
};

export default CollaboratorBar;
